import type { components } from '../../lib/api/openapi-schema';
import {
  getBirthdaySettingsOrDefault,
  getCommunicationSettingsOrDefault,
} from '../business/communication-settings';

export interface BusinessReference {
  id: number;
  name: string;
  location: string | null;
  email?: string;
  phone?: string;
  slug?: string;
  timezone?: string;
  description?: string | null;
  photoUrl?: string | null;
  role?: components['schemas']['AccessibleBusiness']['role'];
  permissions?: components['schemas']['StaffPermissions'];
  communicationSettings?: components['schemas']['CommunicationSettings'];
  birthdaySettings?: components['schemas']['BirthdaySettings'];
}

export interface BusinessSnapshot extends BusinessReference {
  services: components['schemas']['Service'][];
  availabilities: components['schemas']['Availability'][];
  recurring_breaks: components['schemas']['RecurringBreak'][];
}

export interface AuthSession {
  token: string | null;
  userId?: number;
  userType?: components['schemas']['UserType'];
  email: string;
  name: string | null;
  rememberMe: boolean;
  hasBusiness: boolean;
  accessibleBusinesses?: BusinessReference[];
  businessReference?: BusinessReference;
  businessSnapshot?: BusinessSnapshot;
}

const LOCAL_STORAGE_KEY = 'agendify.auth.session';
const SESSION_STORAGE_KEY = 'agendify.auth.session.temp';

function isStorageAvailable(storage: Storage) {
  try {
    const key = '__agendify_probe__';
    storage.setItem(key, '1');
    storage.removeItem(key);
    return true;
  } catch (error) {
    return false;
  }
}

function getNestedRecord(value: unknown) {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function getString(record: Record<string, unknown>, key: string) {
  const value = record[key];
  return typeof value === 'string' ? value : null;
}

function getNullableString(record: Record<string, unknown>, key: string) {
  const value = record[key];

  if (value === null) {
    return null;
  }

  return typeof value === 'string' ? value : undefined;
}

function getNumber(record: Record<string, unknown>, key: string) {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function getFlexibleNumber(record: Record<string, unknown>, key: string) {
  const value = record[key];

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function getStringifiedNumber(record: Record<string, unknown>, key: string) {
  const value = record[key];

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return null;
}

function getBoolean(record: Record<string, unknown>, key: string) {
  const value = record[key];
  return typeof value === 'boolean' ? value : null;
}

function getAccessibleBusinessRole(
  record: Record<string, unknown>,
  key: string
): components['schemas']['AccessibleBusiness']['role'] | undefined {
  const value = record[key];

  return value === 'owner' || value === 'staff' ? value : undefined;
}

type ScopeNoneAll = 'none' | 'all';
type ScopeNoneOwnAll = 'none' | 'own' | 'all';

function getScopeNoneAll(record: Record<string, unknown>, key: string): ScopeNoneAll {
  const v = record[key];
  return v === 'none' || v === 'all' ? v : 'all';
}

function getScopeNoneOwnAll(record: Record<string, unknown>, key: string): ScopeNoneOwnAll {
  const v = record[key];
  return v === 'none' || v === 'own' || v === 'all' ? v : 'all';
}

export function normalizeStaffPermissions(
  value: unknown
): components['schemas']['StaffPermissions'] | undefined {
  const r = getNestedRecord(value);
  if (!r) return undefined;

  const business = getNestedRecord(r.business ?? null);
  const dashboard = getNestedRecord(r.dashboard ?? null);
  const appointments = getNestedRecord(r.appointments ?? null);
  const clients = getNestedRecord(r.clients ?? null);
  const staff = getNestedRecord(r.staff ?? null);
  const services = getNestedRecord(r.services ?? null);
  const availabilities = getNestedRecord(r.availabilities ?? null);
  const breaks = getNestedRecord(r.breaks ?? null);
  const spaces = getNestedRecord(r.spaces ?? null);
  const loyaltyPrograms = getNestedRecord(r.loyalty_programs ?? null);

  if (
    !business || !dashboard || !appointments || !clients ||
    !staff || !services || !availabilities || !breaks || !spaces || !loyaltyPrograms
  ) {
    return undefined;
  }

  return {
    business: {
      read: getScopeNoneAll(business, 'read'),
      update: getScopeNoneAll(business, 'update'),
    },
    dashboard: {
      read: getScopeNoneOwnAll(dashboard, 'read'),
    },
    appointments: {
      read: getScopeNoneOwnAll(appointments, 'read'),
      create: getScopeNoneOwnAll(appointments, 'create'),
      update: getScopeNoneOwnAll(appointments, 'update'),
      delete: getScopeNoneOwnAll(appointments, 'delete'),
    },
    clients: {
      read: getScopeNoneAll(clients, 'read'),
      create: getScopeNoneAll(clients, 'create'),
      update: getScopeNoneAll(clients, 'update'),
      delete: getScopeNoneAll(clients, 'delete'),
      restore: getScopeNoneAll(clients, 'restore'),
    },
    staff: {
      read: getScopeNoneOwnAll(staff, 'read'),
      update: getScopeNoneOwnAll(staff, 'update'),
      create: getScopeNoneAll(staff, 'create'),
      delete: getScopeNoneAll(staff, 'delete'),
      restore: getScopeNoneAll(staff, 'restore'),
    },
    services: {
      read: getScopeNoneAll(services, 'read'),
      create: getScopeNoneAll(services, 'create'),
      update: getScopeNoneAll(services, 'update'),
      delete: getScopeNoneAll(services, 'delete'),
      restore: getScopeNoneAll(services, 'restore'),
    },
    availabilities: {
      read: getScopeNoneOwnAll(availabilities, 'read'),
      create: getScopeNoneOwnAll(availabilities, 'create'),
      update: getScopeNoneOwnAll(availabilities, 'update'),
      delete: getScopeNoneOwnAll(availabilities, 'delete'),
    },
    breaks: {
      read: getScopeNoneOwnAll(breaks, 'read'),
      create: getScopeNoneOwnAll(breaks, 'create'),
      update: getScopeNoneOwnAll(breaks, 'update'),
      delete: getScopeNoneOwnAll(breaks, 'delete'),
    },
    spaces: {
      read: getScopeNoneAll(spaces, 'read'),
      create: getScopeNoneAll(spaces, 'create'),
      update: getScopeNoneAll(spaces, 'update'),
      delete: getScopeNoneAll(spaces, 'delete'),
      restore: getScopeNoneAll(spaces, 'restore'),
    },
    loyalty_programs: {
      read: getScopeNoneAll(loyaltyPrograms, 'read'),
      create: getScopeNoneAll(loyaltyPrograms, 'create'),
      update: getScopeNoneAll(loyaltyPrograms, 'update'),
      delete: getScopeNoneAll(loyaltyPrograms, 'delete'),
    },
  };
}

function dedupeBusinesses(businesses: BusinessReference[]) {
  const seenIds = new Set<number>();

  return businesses.filter((business) => {
    if (seenIds.has(business.id)) {
      return false;
    }

    seenIds.add(business.id);
    return true;
  });
}

function getUserType(record: Record<string, unknown>, key: string) {
  const value = record[key];

  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase();

  if (normalized === 'owner') {
    return 'Owner';
  }

  if (normalized === 'staff') {
    return 'Staff';
  }

  return null;
}

function getServiceBookingMode(
  record: Record<string, unknown>
): components['schemas']['Service']['booking_mode'] {
  const value = record.booking_mode;

  return value === 'all_assigned_staff' ? 'all_assigned_staff' : 'single_staff';
}

function normalizeService(value: unknown): components['schemas']['Service'] | null {
  const record = getNestedRecord(value);

  if (!record) {
    return null;
  }

  const id = getNumber(record, 'id');
  const name = getString(record, 'name');
  const durationMins = getNumber(record, 'duration_mins');
  const price = getStringifiedNumber(record, 'price');
  const rawStaffIds = Array.isArray(record.staff_ids) ? record.staff_ids : [];
  const staffIds = rawStaffIds
    .map((staffId) =>
      typeof staffId === 'number' && Number.isFinite(staffId)
        ? staffId
        : typeof staffId === 'string' && staffId.trim()
          ? Number(staffId)
          : null
    )
    .filter((staffId): staffId is number => staffId !== null && staffId > 0);
  const rawSpaceIds = Array.isArray(record.space_ids) ? record.space_ids : [];
  const spaceIds = rawSpaceIds
    .map((spaceId) =>
      typeof spaceId === 'number' && Number.isFinite(spaceId)
        ? spaceId
        : typeof spaceId === 'string' && spaceId.trim()
          ? Number(spaceId)
          : null
    )
    .filter((spaceId): spaceId is number => spaceId !== null && spaceId > 0);
  const publicBookingEnabled =
    getBoolean(record, 'public_booking_enabled') ?? false;
  const bookingMode = getServiceBookingMode(record);
  const archivedAt = getNullableString(record, 'archived_at');
  const createdAt = getString(record, 'created_at') ?? '';
  const updatedAt = getString(record, 'updated_at') ?? createdAt;

  if (id === null || name === null || durationMins === null || price === null) {
    return null;
  }

  return {
    id,
    name,
    duration_mins: durationMins,
    booking_mode: bookingMode,
    price,
    staff_ids: staffIds,
    space_ids: spaceIds,
    public_booking_enabled: publicBookingEnabled,
    archived_at: archivedAt,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

function normalizeAvailability(
  value: unknown
): components['schemas']['Availability'] | null {
  const record = getNestedRecord(value);

  if (!record) {
    return null;
  }

  const id = getNumber(record, 'id');
  const wday = getNumber(record, 'wday');
  const active = getBoolean(record, 'active');
  const createdAt = getString(record, 'created_at');
  const updatedAt = getString(record, 'updated_at');
  const opensAtValue = record.opens_at;
  const closesAtValue = record.closes_at;
  const opensAt =
    typeof opensAtValue === 'string' || opensAtValue === null ? opensAtValue : undefined;
  const closesAt =
    typeof closesAtValue === 'string' || closesAtValue === null ? closesAtValue : undefined;

  if (
    id === null ||
    wday === null ||
    active === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  return {
    id,
    wday,
    opens_at: opensAt,
    closes_at: closesAt,
    active,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

function normalizeRecurringBreak(
  value: unknown
): components['schemas']['RecurringBreak'] | null {
  const record = getNestedRecord(value);

  if (!record) {
    return null;
  }

  const id = getNumber(record, 'id');
  const wday = getNumber(record, 'wday');
  const startsAt = getString(record, 'starts_at');
  const endsAt = getString(record, 'ends_at');
  const reason = getString(record, 'reason') ?? '';
  let staffId: number | null = null;

  if (record.staff_id !== undefined && record.staff_id !== null) {
    const parsedStaffId = getFlexibleNumber(record, 'staff_id');

    if (parsedStaffId === null) {
      return null;
    }

    staffId = parsedStaffId;
  }

  if (id === null || wday === null || startsAt === null || endsAt === null) {
    return null;
  }

  return {
    id,
    staff_id: staffId,
    wday,
    starts_at: startsAt,
    ends_at: endsAt,
    reason,
  };
}

export function normalizeBusinessSnapshot(value: unknown): BusinessSnapshot | null {
  const record = getNestedRecord(value);

  if (!record) {
    return null;
  }

  const businessReference = normalizeBusinessReference(record);

  if (!businessReference) {
    return null;
  }

  const rawServices = Array.isArray(record.services) ? record.services : null;
  const rawAvailabilities = Array.isArray(record.availabilities)
    ? record.availabilities
    : null;
  const rawRecurringBreaks = Array.isArray(record.recurring_breaks)
    ? record.recurring_breaks
    : [];

  if (!rawServices || !rawAvailabilities) {
    return null;
  }

  const services = rawServices
    .map((service) => normalizeService(service))
    .filter((service): service is components['schemas']['Service'] => service !== null);
  const availabilities = rawAvailabilities
    .map((availability) => normalizeAvailability(availability))
    .filter(
      (availability): availability is components['schemas']['Availability'] =>
        availability !== null
    );
  const recurringBreaks = rawRecurringBreaks
    .map((recurringBreak) => normalizeRecurringBreak(recurringBreak))
    .filter(
      (recurringBreak): recurringBreak is components['schemas']['RecurringBreak'] =>
        recurringBreak !== null
    );

  if (
    services.length !== rawServices.length ||
    availabilities.length !== rawAvailabilities.length ||
    recurringBreaks.length !== rawRecurringBreaks.length
  ) {
    return null;
  }

  return {
    ...businessReference,
    services,
    availabilities,
    recurring_breaks: recurringBreaks,
  };
}

export function normalizeBusinessReference(value: unknown): BusinessReference | null {
  const record = getNestedRecord(value);

  if (!record) {
    return null;
  }

  const id = getNumber(record, 'id');
  const name = getString(record, 'name');
  const location = getNullableString(record, 'location') ?? null;
  const email = getString(record, 'email') ?? undefined;
  const phone = getString(record, 'phone') ?? undefined;
  const slug = getString(record, 'slug') ?? undefined;
  const timezone = getString(record, 'timezone') ?? undefined;
  const description = getNullableString(record, 'description');
  const photoUrl =
    getNullableString(record, 'photo_url') ??
    getNullableString(record, 'photoUrl');
  const role = getAccessibleBusinessRole(record, 'role');
  const permissions = normalizeStaffPermissions(record.permissions ?? null);
  const communicationSettings = getCommunicationSettingsOrDefault(
    record.communication_settings ?? record.communicationSettings ?? null
  );
  const birthdaySettings = getBirthdaySettingsOrDefault(
    record.birthday_settings ?? record.birthdaySettings ?? null
  );

  if (id === null || name === null) {
    return null;
  }

  return {
    id,
    name,
    location,
    ...(email ? { email } : {}),
    ...(phone ? { phone } : {}),
    ...(slug ? { slug } : {}),
    ...(timezone ? { timezone } : {}),
    ...(description !== undefined ? { description } : {}),
    ...(photoUrl !== undefined ? { photoUrl } : {}),
    ...(role ? { role } : {}),
    ...(permissions ? { permissions } : {}),
    communicationSettings,
    birthdaySettings,
  };
}

export function normalizeBusinessReferences(value: unknown): BusinessReference[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return dedupeBusinesses(
    value
      .map((business) => normalizeBusinessReference(business))
      .filter((business): business is BusinessReference => business !== null)
  );
}

export function resolveBusinessRoleUserType(
  role: BusinessReference['role'] | undefined
): components['schemas']['UserType'] | undefined {
  if (role === 'owner') {
    return 'Owner';
  }

  if (role === 'staff') {
    return 'Staff';
  }

  return undefined;
}

export function resolveSessionBusinessUserType(
  session:
    | Pick<AuthSession, 'userType' | 'businessReference' | 'businessSnapshot'>
    | null
    | undefined
): components['schemas']['UserType'] | undefined {
  const activeRole =
    session?.businessSnapshot?.role ?? session?.businessReference?.role;

  return resolveBusinessRoleUserType(activeRole) ?? session?.userType;
}

function readSession(storage: Storage) {
  const rawValue = storage.getItem(
    storage === window.localStorage ? LOCAL_STORAGE_KEY : SESSION_STORAGE_KEY
  );

  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as Partial<AuthSession>;
    const parsedRecord = getNestedRecord(parsed);

    if (!parsedRecord || typeof parsed.email !== 'string') {
      storage.removeItem(
        storage === window.localStorage ? LOCAL_STORAGE_KEY : SESSION_STORAGE_KEY
      );
      return null;
    }

    const businessSnapshot = normalizeBusinessSnapshot(parsed.businessSnapshot);
    const businessReference =
      businessSnapshot ??
      normalizeBusinessReference(parsed.businessReference);
    const accessibleBusinesses = normalizeBusinessReferences(
      parsedRecord.accessibleBusinesses
    );

    return {
      token: typeof parsed.token === 'string' ? parsed.token : null,
      userId: getFlexibleNumber(parsedRecord, 'userId') ?? undefined,
      userType: getUserType(parsedRecord, 'userType') ?? undefined,
      email: parsed.email,
      name: typeof parsed.name === 'string' ? parsed.name : null,
      rememberMe: parsed.rememberMe === true,
      hasBusiness: parsed.hasBusiness === true,
      accessibleBusinesses:
        accessibleBusinesses.length > 0 ? accessibleBusinesses : undefined,
      businessReference: businessReference ?? undefined,
      businessSnapshot: businessSnapshot ?? undefined,
    } satisfies AuthSession;
  } catch (error) {
    storage.removeItem(
      storage === window.localStorage ? LOCAL_STORAGE_KEY : SESSION_STORAGE_KEY
    );
    return null;
  }
}

export function getStoredSession() {
  if (typeof window === 'undefined') {
    return null;
  }

  if (isStorageAvailable(window.localStorage)) {
    const localSession = readSession(window.localStorage);
    if (localSession) {
      return localSession;
    }
  }

  if (isStorageAvailable(window.sessionStorage)) {
    return readSession(window.sessionStorage);
  }

  return null;
}

export function persistSession(session: AuthSession) {
  const targetStorage =
    session.rememberMe && isStorageAvailable(window.localStorage)
      ? window.localStorage
      : window.sessionStorage;
  const fallbackStorage =
    targetStorage === window.localStorage
      ? window.sessionStorage
      : window.localStorage;
  const targetKey =
    targetStorage === window.localStorage ? LOCAL_STORAGE_KEY : SESSION_STORAGE_KEY;
  const fallbackKey =
    fallbackStorage === window.localStorage
      ? LOCAL_STORAGE_KEY
      : SESSION_STORAGE_KEY;

  targetStorage.setItem(targetKey, JSON.stringify(session));
  fallbackStorage.removeItem(fallbackKey);
}

export function clearSession() {
  if (typeof window === 'undefined') {
    return;
  }

  if (isStorageAvailable(window.localStorage)) {
    window.localStorage.removeItem(LOCAL_STORAGE_KEY);
  }

  if (isStorageAvailable(window.sessionStorage)) {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }
}
